import Page from '../page';
import SaveStatus, { Status } from '../../components/common/save-status';
import { formatCurrency } from '../../helpers/utils';
import debounce from '../../helpers/debounce';

const selectors = {
  form: '.js-submitAutopartsForm',
  quantitySelector: '.js-qty-selector-input',
  entryRow: '.js-entryRow',
  rowTotal: '.js-rowTotal',
  setMaxQuantities: '.js-setMaxQuantities',
  netTotal: '.js-netTotal',
  taxTotal: '.js-taxTotal',
  grandTotal: '.js-grandTotal',
};

export default class WebReturnsAutopartsDetails extends Page {

  private readonly form: JQuery<HTMLFormElement>;
  private readonly setMaxQuantitiesButtons: JQuery<HTMLButtonElement>;
  private readonly quantitySelectors: JQuery<HTMLInputElement>;
  private readonly saveStatus: SaveStatus;

  private readonly netTotal: JQuery<HTMLSpanElement>;
  private readonly taxTotal: JQuery<HTMLSpanElement>;
  private readonly grandTotal: JQuery<HTMLSpanElement>;

  constructor(private readonly body: JQuery) {
    super();

    this.form = body.find<HTMLFormElement>(selectors.form);
    this.setMaxQuantitiesButtons = body.find<HTMLButtonElement>(selectors.setMaxQuantities);
    this.quantitySelectors = body.find<HTMLInputElement>(selectors.quantitySelector);
    this.saveStatus = new SaveStatus(body[0].querySelector('.js-saveStatus') as HTMLDivElement);

    this.netTotal = this.body.find<HTMLSpanElement>(selectors.netTotal);
    this.taxTotal = this.body.find<HTMLSpanElement>(selectors.taxTotal);
    this.grandTotal = this.body.find<HTMLSpanElement>(selectors.grandTotal);

    this.bindChangeQuantity();
    this.bindAllQuantitiesToMax();
  }

  private bindChangeQuantity(): void {
    this.body.find<HTMLInputElement>(selectors.quantitySelector).on('input', (e) => {
      const row = $(e.target).closest(selectors.entryRow);
      this.updateRowTotal(row);
      this.handleChange();
    });
  }

  private bindAllQuantitiesToMax(): void {
    this.body.find(selectors.setMaxQuantities).on('click', () => {
      const quantities = $(selectors.quantitySelector);
      quantities.each((_, element) => {
        const quantity = $(element);
        quantity.val(quantity.data('max')).trigger('input');
      });
    });
    this.toggleMaxButtonState();
  }

  private saveAutopartsReturn(): void {
    const formData = new FormData(this.form[0]);
    const data = new URLSearchParams(formData as any); // no File inputs, cast to any is safe: https://github.com/microsoft/TypeScript/issues/30584

    fetch(`${window.ACC.config.encodedContextPath}/webreturns/autopart/save`, {
      method: 'post',
      body: data,
      credentials: 'include',
    }).then(() => {
      this.saveStatus.setStatus(Status.Saved);
    }).catch((e) => {
      // eslint-disable-next-line no-console
      console.log(e);
      this.saveStatus.setStatus(Status.Saved);
    });
  }

  private debouncedSave = debounce(() => this.saveAutopartsReturn(), 500);

  private updateRowTotal = (row: JQuery): void => {
    const quantityInput = row.find<HTMLInputElement>(selectors.quantitySelector);
    const value = Number.parseInt(quantityInput.val() as string, 10);
    const rowTotalSpan = row.find(selectors.rowTotal);
    const pricePerPiece = Number.parseFloat(rowTotalSpan.data('price-per-piece'));
    rowTotalSpan.text((value * pricePerPiece).toFixed(2));
  };

  private updateTotals(): void {
    const netTotal = this.body.find(selectors.rowTotal)
      .toArray()
      .reduce((acc, curr) => acc + Number.parseFloat(curr.textContent ?? '0'), 0);

    const taxTotal = Math.round(netTotal * ACC.taxRate * 100) / 100;
    const grossTotal = (netTotal + taxTotal);

    this.netTotal.text(formatCurrency(netTotal));
    this.taxTotal.text(formatCurrency(taxTotal));
    this.grandTotal.text(formatCurrency(grossTotal));
  }

  private areAllQuantitiesAtMax(): boolean {
    return !this.quantitySelectors
      .toArray()
      .some((qtyEl) => (Number.parseInt(qtyEl.value, 10) !== Number.parseInt(qtyEl.dataset.max ?? '0', 10)));
  }

  private toggleMaxButtonState(): void {
    if (this.areAllQuantitiesAtMax()) {
      this.setMaxQuantitiesButtons
        .prop('disabled', true)
        .removeClass('-secondary')
        .addClass('-primary');
    } else {
      this.setMaxQuantitiesButtons
        .prop('disabled', false)
        .removeClass('-primary')
        .addClass('-secondary');
    }
  }

  private handleChange(): void {
    this.saveStatus.setStatus(Status.Saving);
    this.debouncedSave();
    this.updateTotals();
    this.toggleMaxButtonState();
  }
}
